import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NotificationsModal, SearchOverlay, type NotificationRow, type SearchGroup } from '@/components/kit';
import { useNotifications, useMarkAsRead } from '@/hooks/useNotifications';
import { usePerformed, useWorklist } from '../api';
import { clock, dayMonth, drName, shortName } from '../format';

const en = (f: unknown): string =>
  typeof f === 'string' ? f : ((f as { en?: string } | null)?.en ?? '');

/**
 * SEARCH AND BELL — live overlays scoped to this role's own entities (the
 * canvas's demo path 8): today's tests, my performed records, the machines.
 * Navigating out of one closes it.
 */
export function useTechOverlays() {
  const navigate = useNavigate();
  const [search, setSearch] = useState(false);
  const [bell, setBell] = useState(false);
  const list = useWorklist();
  const performed = usePerformed({ enabled: search });
  const since = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }, []);
  const notes = useNotifications(bell ? { fromDate: since, limit: 50 } : { fromDate: since, limit: 1 });
  const markRead = useMarkAsRead();

  const go = (to: string, state?: unknown) => {
    setSearch(false);
    setBell(false);
    navigate(to, state ? { state } : undefined);
  };

  const groups: SearchGroup[] = useMemo(() => {
    const tasks = list.data?.tasks ?? [];
    const devices = list.data?.devices ?? [];
    return [
      {
        key: 'worklist',
        label: 'On the worklist',
        hits: tasks.map((t) => ({
          key: t.id,
          name: `${t.title} · ${shortName(t.patient)}`,
          sub: [t.patient?.mrn ? `MRN ${t.patient.mrn}` : '', drName(t.requestedBy), t.state === 'done' ? 'sent' : t.lane.replace('_', ' ')]
            .filter(Boolean).join(' · '),
          where: t.state === 'open' ? 'Worklist' : 'Capture',
          onGo: () => (t.state === 'open' ? go('/technician/worklist', { select: t.id }) : go(`/technician/worklist/${t.id}`)),
        })),
      },
      {
        key: 'performed',
        label: 'Performed this month',
        hits: (performed.data ?? []).map((r) => ({
          key: `p:${r.id}`,
          name: `${r.title} · ${shortName(r.patient)}`,
          sub: `${dayMonth(r.completedAt)} · ${clock(r.completedAt)}`,
          where: 'My day',
          onGo: () => go(`/technician/worklist/${r.id}`),
        })),
      },
      {
        key: 'machines',
        label: 'Machines',
        hits: devices.map((d) => ({
          key: `d:${d.id}`,
          name: d.name,
          sub: [d.room, d.status === 'down' ? 'down' : 'online'].filter(Boolean).join(' · '),
          where: 'Worklist',
          onGo: () => go('/technician/worklist'),
        })),
      },
    ].filter((g) => g.hits.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data, performed.data]);

  /* Every row lands somewhere real: the test it names, else the worklist.
     The texts the technician receives never name a patient. */
  const rows: NotificationRow[] = (bell ? notes.data?.data ?? [] : [])
    /* "What rang today" — the list endpoint's date filter is not relied on. */
    .filter((n) => new Date(n.createdAt).getTime() >= new Date(since).getTime())
    .map((n) => {
    const taskId = (n.metadata as { technicianTaskId?: string } | undefined)?.technicianTaskId;
    return {
      key: n.id,
      what: (
        <>
          <p className={`t-body${n.readAt ? '' : ' w6'}`}>{en(n.title)}</p>
          <p className="t-cap mut">{en(n.message)} · {clock(n.createdAt)}</p>
        </>
      ),
      where: taskId ? 'Capture' : 'Worklist',
      onGo: () => {
        if (!n.readAt) markRead.mutate(n.id);
        go(taskId ? `/technician/worklist/${taskId}` : '/technician/worklist');
      },
    };
  });

  const overlays = (
    <>
      <SearchOverlay
        open={search}
        onClose={() => setSearch(false)}
        groups={groups}
        placeholder="Orders, patients, MRN, machines…"
        scopeNote="today’s worklist, what you performed this month, and the machines — nothing else"
      />
      <NotificationsModal
        open={bell}
        onClose={() => setBell(false)}
        rows={rows}
        sub="what rang today — every row lands on its screen"
      />
    </>
  );
  return { openSearch: () => setSearch(true), openBell: () => setBell(true), overlays };
}
